export const LANG = {

	in<T>(left: T, ...right: T[]) {
		for (let i = 0; i < right.length; i++) if (left === right[i]) return true;
		return false;
	},

	/** Egalité stricte de 2 tableaux. */
	arrayEquals: function (a: Array<any> | null, b: Array<any> | null): boolean {
		if (a == null || b == null) return a == b;
		return a.length === b.length && a.every((v, i) => v === b[i]);
	},

	/** Ajoute des membres à un tableau si ils ne sont pas undefined. */
	pushIfDefined<T>(arr: T[], ...members: (T | undefined)[]): T[] {
		for (let i = 0, s = members.length; i < s; i++) {
			const v = members[i];
			if (v !== undefined) arr.push(v);
		}
		return arr;
	},

	/**
	 * Copie toutes les ownProps d'un prototype d'une classe sur un prototype d'une autre classe
	 * si cette propriétée n'est pas déjà spécifiée sur la classe cible.
	 *
	 * ATTENTION : Si la chaine d'héritage diffère, les functions copiées NE doivent PAS contenir
	 * le mot cle "super" (qui est résolu statiquement).
	 *
	 */
	completeClassProps(fromClass: any, toClass: any) {
		const from = fromClass.prototype;
		const to = toClass.prototype;
		for (const key of Object.getOwnPropertyNames(from)) {
			if (key !== "constructor") {
				if (!to.hasOwnProperty(key)) Object.defineProperty(to, key, Object.getOwnPropertyDescriptor(from, key));
			}
		}
	},

	/** Insert une string dans une autre. */
	stringInsert(str: string, offset: number, ins: string) {
		if (str == null) return ins;
		if (offset === 0) {
			return ins + str;
		} else if (offset === str.length) {
			return str + ins;
		}
		return str.substring(0, offset) + ins + str.substring(offset);
	},

	/** Supprime un fragment de string. */
	stringDelete(str: string, offset: number, len: number) {
		if (len === 0) return str;
		if (offset === 0) {
			return str.substring(len);
		} else if (offset + len >= str.length) {
			return str.substring(0, offset);
		} else {
			return str.substring(0, offset) + str.substring(offset + len);
		}
	},


	/**
	 * Implémentation partielle de l'API Formatter.
	 *
	 * Usage :
	 *   LANG.formatStr("test %2$s %1$s %s %s %%", "a", "b");
	 *   retourne : "test b a a b %"
	 */
	formatStr(text: string, vars: string[]) {
		if (!text || !vars) return text;
		let idx = 0;
		return text.replace(/(%(?:%|(?:(\d+)\$)?s))/g,
			(substring: string, p1: string, p2: string) => p1 == "%%" ? "%" : vars[(p2 ? parseInt(p2) - 1 : idx++)]
		);
	},

	/** Echappe une chaine de caractère pour être insérée dans le pattern d'une RegExp. */
	escape4Regexp(text: string): string {
		return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
	},

	/**
	 * Echappe une chaine de caractère pour être insérée dans le pattern d'une RegExp en intégrant la recherche
	 * floue sur les caractères similaires (dépendant de la locale courante).
	 */
	escape4RegexpFuzzy(text: string): string {
		const chars = [] as string[];
		const dic = this.fuzzySearch();
		for (let i = 0; i < text.length; i++) {
			const ch = text.charAt(i);
			const fuzzy = dic.get(ch.charCodeAt(0));
			if (fuzzy !== undefined) {
				chars.push('[', fuzzy, ']');
			} else {
				chars.push(this.escape4Regexp(ch));
			}
		}
		return chars.join('');
	},

	escapeChar4Regexp(char: string): string {
		const val = char.charCodeAt(0).toString(16);
		switch (val.length) {
		case 1:
			return "\\x0" + val;
		case 2:
			return "\\x" + val;
		case 3:
			return "\\u0" + val;
		case 4:
			return "\\u" + val;
		}
	},

	_fuzzySearchDatas: "\xA0 |aâàä|eéèêë|iîï|oöô|uùûü|cç|AÀÂÄ|EÈÉÊË|IÎÏ|UÙÛÜ|CÇ",

	_fuzzySearch: null as Map<number, string>,
	fuzzySearch(): Map<number, string> {
		if (this._fuzzySearch === null) {
			const dic = this._fuzzySearch = new Map<number, string>();
			for (const group of this._fuzzySearchDatas.split("|")) {
				for (let i = 0; i < group.length; i++) dic.set(group.charCodeAt(i), group);
			}
		}
		return this._fuzzySearch;
	},

	/**
	 * Map un tableau vers un ensemble de promesses résolues en concurrentiel
	 * @param array Le tableau à mapper puis résoudre
	 * @param mapper La fonction de mappage item vers promesse
	 * @param concurrency Nombre de promesses à résoudre en parallèle
	 * @returns Un tableau des résultats des promesses dans l'ordre du tableau d'entrée
	 */
	promisesMap: async function <T, R>(
		array: ArrayLike<T>,
		mapper: (item: T, index: number) => Promise<R>,
		concurrency: number): Promise<R[]> {

		const results = Array<R>(array.length);

		// Initialisation du pool
		const poolSize = Math.min(concurrency, array.length);
		const pool = new Array<Promise<any>>(poolSize);
		// Les promesses sont augmentées pour stocker le résultat puis retourner leur index dans le pool
		for (let i = 0; i < poolSize; i++) pool[i] = mapper(array[i], i).then((result) => {
			results[i] = result;
			return i
		});

		let activeCount = poolSize;
		let i = poolSize;

		while (activeCount) {
			// Utilisation d'un pool filtré pour la course lors du désemplissement
			const racePool = activeCount === poolSize ? pool : pool.filter((p) => p);
			const resolvedIdx = await Promise.race(racePool);

			if (i < array.length) {
				// Remplacement dans le pool de la promesse résolue par une nouvelle promesse augmentée
				((index: number) => {
					pool[resolvedIdx] = mapper(array[index], index).then((result) => {
						results[index] = result;
						return resolvedIdx;
					});
				})(i);

				i++;
			} else {
				pool[resolvedIdx] = null;
				activeCount--;
			}
		}
		return results;
	},

	/**
	 * Transforme un tableau en dictionnaire. La clé est construit par une fonction en paramètre
	 * @param array Tableau à transformer
	 * @param getKey Fonction retournant la clé associée à chaque entrée du tableau, si retourne null l'entrée est éliminée.
	 */
	reduceToDict<T>(array: T[], getKey: (index: number, array?: T[]) => string | null): Dict<T> {
		return array.reduce<Dict<T>>((dict, item, index, array) => {
			const key = getKey(index, array);
			if (key != null) dict[key] = item;
			return dict;
		}, {})
	}
};


export const enum EBytesUnits {
	bytes, ko, mo, go, to, po
}

export const LOCALE = {
	/**
	 * Retourne le code de langue dans la lanhue courante de l'application
	 * @param language : fr, fr-FR, ...
	 * FIXME : exploiter Intl.DisplayNames de ECMAScript 2020
	 */
	languageToName: function (language: string): string {
		const baseLang = language.split("-")[0];
		if (ISOLANGLIST.has(baseLang))
			return ISOLANGLIST.get(baseLang).name;
		return language;
	},
	/**
	 * Retourne le code de langue dans la langue native du language
	 * @param language : fr, fr-FR, ...
	 * FIXME : exploiter Intl.DisplayNames de ECMAScript 2020
	 */
	nativeLanguageToName: function (language: string): string {
		const baseLang = language.split("-")[0];
		if (ISOLANGLIST.has(baseLang))
			return ISOLANGLIST.get(baseLang).nativeName;
		return language;
	},

	/** Fonction de comparaison par défaut adapté à la locale pour le tri. */
	SORT_CMP: Intl.Collator('fr'),

	/**
	 * Libellé d'une durée approximative entre deux dates.
	 * @param addSign n'affiche aucun sign, le sign '-' si négatif, et/ou '+' si positif.
	 */
	formatMinDuration: function (date1: Date, date2: Date, shortFormat?: boolean, addSign?: '-' | '+-'): string {
		const diffSec = Math.abs(date2.getTime() - date1.getTime()) / 1000;
		const sign = addSign ? (date2.getTime() > date1.getTime() ? addSign.indexOf('+') >= 0 ? '+' : '' : addSign.indexOf('-') >= 0 ? '-' : '') : '';

		const diffYear = Math.floor(diffSec / 31536000); //365 * 86400
		if (diffYear >= 1) return shortFormat ? `${sign}${diffYear}a.` : diffYear > 1 ? `${sign}${diffYear} ans` : `1 an`;

		const diffMonth = Math.floor(diffSec / 2592000); //30 * 86400
		if (diffMonth >= 1) return shortFormat ? `${sign}${diffMonth}M.` : diffMonth > 1 ? `${sign}${diffMonth} mois` : `1 mois`;

		const diffDay = Math.floor(diffSec / 86400);
		if (diffDay >= 7) {
			const week = Math.floor(diffDay / 7);
			return shortFormat ? `${sign}${week}sem.` : diffDay >= 14 ? `${sign}${week} semaines` : `1 semaine`;
		}
		if (diffDay >= 1) return shortFormat ? `${sign}${diffDay}j.` : diffDay > 1 ? `${sign}${diffDay} jours` : `1 jour`;

		const diffHour = Math.floor(diffSec / 3600);
		if (diffHour >= 1) return shortFormat ? `${sign}${diffHour}h.` : diffHour > 1 ? `${sign}${diffHour} heures` : `1 heure`;

		const diffMin = Math.floor(diffSec / 60);
		if (diffMin >= 1) return shortFormat ? `${sign}${diffMin}min.` : diffMin > 1 ? `${sign}${diffMin} minutes` : `1 minute`;

		return ""; // délai < 60s trop court
	},

	/**
	 * Donne une forme lisible string d'un number de bytes
	 * @param bytes
	 * @param forceUnit : force l'unitée désirée. Par défaut, l'unité la plus pertinente est utilisée
	 * @param showUnity : affiche/masque l'unité
	 * @param numberOptions : paramètres d'affichage du chiffre
	 */
	formatByteSize(bytes: number, forceUnit?: EBytesUnits, showUnity: boolean = true, numberOptions?: Intl.NumberFormatOptions): string {
		let scaleLevel: EBytesUnits = 0;
		if (bytes == null || bytes < 0) return "-";
		while ((bytes > 1024 || (forceUnit && scaleLevel < forceUnit)) && scaleLevel < EBytesUnits.po) {
			bytes = bytes / 1024
			scaleLevel++;
		}
		const bytesStr = bytes.toLocaleString("fr", numberOptions || {maximumFractionDigits: 2});
		if (!showUnity) return bytesStr;

		let scaleStr;
		switch (scaleLevel) {
		case EBytesUnits.bytes :
			scaleStr = bytes > 1 ? "Octets" : "Octet";
			break;
		case EBytesUnits.ko :
			scaleStr = "Ko";
			break;
		case EBytesUnits.mo :
			scaleStr = "Mo";
			break;
		case EBytesUnits.go :
			scaleStr = "Go";
			break;
		case EBytesUnits.to :
			scaleStr = "To";
			break;
		case EBytesUnits.po :
			scaleStr = "Po";
			break;
		default:
			scaleStr = "-";
			break;
		}
		return `${bytesStr} ${scaleStr}`;
	},

	/**
	 * Formatter d'une date sous forme numérique avec précision jusqu'à la seconde.
	 */
	formatDateDigitsToSec: new Intl.DateTimeFormat('fr', {
		year: 'numeric', month: 'numeric', day: 'numeric',
		hour: 'numeric', minute: 'numeric', second: 'numeric',
	})
};

const ISOLANGLIST = new Map<string, { name: string, nativeName: string }>();
ISOLANGLIST.set("fr", {name: "Français", nativeName: "Français"});
ISOLANGLIST.set("en", {name: "Anglais", nativeName: "English"});
ISOLANGLIST.set("es", {name: "Espagnol", nativeName: "Español"});
ISOLANGLIST.set("ar", {name: "Arabe", nativeName: "العربية"});
